import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import BookingSystemAPI, { UserDto, AuthResponse } from '../services/BookingSystemAPI';
import { getRoleFromToken } from '../utils/jwtDecoder';interface AuthContextType {
  user: UserDto | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (userData: RegisterData) => Promise<AuthResponse>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isStaff: () => boolean;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  acceptedTerms: boolean;
  acceptedPrivacyPolicy: boolean;
  companyName?: string;
  customerType?: string; // T-099 (v1.8.24)
  phone?: string; // T-100 (v1.8.24)
  landlinePhone?: string; // T-100 (v1.8.24)
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginErrors, setLoginErrors] = useState<string[]>([]);
  const [api] = useState(() => new BookingSystemAPI());

  // Initialize authentication on app startup
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
          api.setToken(storedToken);
          setToken(storedToken);
          
          // Extract role from JWT token
          const roleFromToken = getRoleFromToken(storedToken);
          console.log('InitAuth: Role from JWT token:', roleFromToken);
          
          // Verify token is still valid by fetching user profile
          const userProfile = await api.getUserProfile();
          console.log('InitAuth: UserProfile received:', userProfile);
          
          // Add role from token to user profile - create new object
          let userWithRole = userProfile;
          if (userProfile && roleFromToken) {
            userWithRole = {
              ...userProfile,
              Role: roleFromToken as 'Customer' | 'Staff' | 'Admin',
              role: roleFromToken as 'Customer' | 'Staff' | 'Admin'
            };
            console.log('InitAuth: Created userWithRole:', userWithRole);
          }
          
          console.log('InitAuth: Setting user with role:', userWithRole);
          setUser(userWithRole);
          console.log('InitAuth: User set complete - verifying:', {
            userSet: userWithRole,
            roleSet: userWithRole?.Role || userWithRole?.role
          });
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Clear invalid token
        api.clearToken();
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [api]);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      console.log('AuthContext: Starting login for', email);
      
      const response = await api.login(email, password);
      console.log('AuthContext: Login response received', response);
      console.log('AuthContext: User object details:', {
        user: response.user,
        role: response.user?.role,
        Role: response.user?.Role,
        roleType: typeof (response.user?.Role || response.user?.role)
      });
      
      if (response.success && response.token && response.user) {
        console.log('AuthContext: Login successful, setting token and user');
        
        // Extract role from JWT token
        const roleFromToken = getRoleFromToken(response.token);
        console.log('AuthContext: Role from JWT token:', roleFromToken);
        
        // Add role from token to user object - create new object
        if (roleFromToken) {
          response.user = {
            ...response.user,
            Role: roleFromToken as 'Customer' | 'Staff' | 'Admin',
            role: roleFromToken as 'Customer' | 'Staff' | 'Admin'
          };
        }
        
        console.log('AuthContext: User object to be stored:', response.user);
        console.log('AuthContext: User.Role:', response.user.Role);
        console.log('AuthContext: User.role:', response.user.role);
        
        setToken(response.token);
        setUser(response.user);
        
        // Store refresh token if provided
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        
        console.log('AuthContext: User state updated', response.user);
        console.log('AuthContext: User role after update:', response.user?.Role || response.user?.role);
        
        // Verify what was actually stored
        setTimeout(() => {
          console.log('AuthContext: Stored user after setState:', user);
        }, 100);
      } else {
        console.log('AuthContext: Login failed', {
          success: response.success,
          hasToken: !!response.token,
          hasUser: !!response.user,
          message: response.message
        });
      }
      
      return response;
    } catch (error: any) {
      console.error('AuthContext: Login failed with error:', error);
      // Return error as response format
      return {
        success: false,
        message: error.message || 'Anmeldung fehlgeschlagen',
        errors: [error.message || 'Ein Fehler ist aufgetreten']
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      const response = await api.register(userData);
      
      if (response.success && response.token && response.user) {
        setToken(response.token);
        setUser(response.user);
        
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    api.logout();
    setToken(null);
    setUser(null);
    localStorage.removeItem('refreshToken');
  };

  const refreshAuth = async (): Promise<void> => {
    try {
      const response = await api.refreshToken();
      if (response.success && response.token) {
        setToken(response.token);
        api.setToken(response.token);
        
        if (response.user) {
          setUser(response.user);
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  // Role-based access control helpers
  const hasRole = (role: string): boolean => {
    if (!user) {
      console.log('hasRole: No user found');
      return false;
    }
    
    // Get the user role (should now be a string after transformation in API)
    const userRole = user.Role || user.role;
    
    const result = userRole === role || user.permissions?.includes(role) || false;
    
    console.log('hasRole check:', {
      checkingFor: role,
      userRole,
      userRoleType: typeof userRole,
      result
    });
    
    return result;
  };

  const isAdmin = (): boolean => {
    const result = hasRole('Admin') || hasRole('SuperAdmin');
    console.log('isAdmin check:', {
      user,
      userRole: user?.Role || user?.role,
      hasRoleAdmin: hasRole('Admin'),
      hasRoleSuperAdmin: hasRole('SuperAdmin'),
      result
    });
    return result;
  };

  const isStaff = (): boolean => {
    return hasRole('Staff') || hasRole('Admin') || hasRole('SuperAdmin');
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    register,
    logout,
    refreshAuth,
    hasRole,
    isAdmin,
    isStaff
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;